<template>
  <v-container fluid class="collegesid-page">
    <!-- ไม่พบ college_ID ในลิงก์ -->
    <v-alert v-if="!college_ID" type="warning" outlined class="mt-4">
      ไม่พบรหัสสถานศึกษาในลิงก์ กรุณาเลือกสถานศึกษาจาก
      <router-link to="/pages/colleges">หน้ารายชื่อสถานศึกษา</router-link>
    </v-alert>

    <template v-else>
      <!-- Hero header -->
      <v-card outlined rounded="lg" class="hero-card mb-4" :loading="heroLoading">
        <v-card-text class="pa-4 pa-md-6">
          <div class="breadcrumb-row mb-2">
            <router-link to="/pages/colleges" class="breadcrumb-link">
              <v-icon x-small color="#B3151B">mdi-arrow-left</v-icon>
              ข้อมูลสถานศึกษา
            </router-link>
          </div>

          <div class="d-flex flex-column flex-md-row align-md-center">
            <div class="flex-grow-1">
              <h1 class="college-name">{{ collegeinfo.college_name || "กำลังโหลดข้อมูล..." }}</h1>
              <div v-if="collegeinfo.college_NameEng" class="college-name-eng">{{ collegeinfo.college_NameEng }}</div>

              <div class="chip-row mt-2">
                <v-chip v-if="collegeinfo.collegetype_name" small label color="#FBECEC" text-color="#B3151B" class="mr-2 mb-1">
                  <v-icon x-small left>mdi-domain</v-icon>{{ collegeinfo.collegetype_name }}
                </v-chip>
                <v-chip v-if="collegeinfo.prefecture_name" small label outlined color="#1B365D" class="mr-2 mb-1">
                  <v-icon x-small left>mdi-map-marker</v-icon>
                  {{ collegeinfo.prefecture_name }} {{ collegeinfo.province_name }}
                </v-chip>
                <v-chip v-if="collegeinfo.region_name" small label outlined color="#1B365D" class="mr-2 mb-1">
                  {{ collegeinfo.region_name }}
                </v-chip>
                <v-chip v-if="collegeinfo.collegeIni" small label outlined color="grey darken-1" class="mr-2 mb-1">
                  {{ collegeinfo.collegeIni }}
                </v-chip>
              </div>
            </div>

            <div class="export-col mt-3 mt-md-0">
              <v-btn color="#B3151B" dark depressed @click="exportExcel" :loading="exporting">
                <v-icon left small>mdi-file-excel</v-icon>
                ส่งออกรายงาน Excel
              </v-btn>
              <div class="text-caption grey--text text-md-right mt-1">
                ข้อมูล ณ วันที่ {{ todayThai }}
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- สรุปสถิติ -->
      <v-row dense class="mb-4">
        <v-col cols="6" sm="4" md="2">
          <v-card outlined rounded="lg" class="pa-3 text-center stat-card">
            <div class="stat-value" style="color:#B3151B">{{ numberWithCommas(totalStudents) }}</div>
            <div class="stat-label">ผู้เรียนทั้งหมด (คน)</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined rounded="lg" class="pa-3 text-center stat-card">
            <div class="stat-value" style="color:#1B365D">{{ numberWithCommas(totalPersonnel) }}</div>
            <div class="stat-label">บุคลากรทั้งหมด (คน)</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined rounded="lg" class="pa-3 text-center stat-card">
            <div class="stat-value" style="color:#A86400">{{ numberWithCommas(totalGovTeachers) }}</div>
            <div class="stat-label">ข้าราชการครูฯ (คน)</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined rounded="lg" class="pa-3 text-center stat-card">
            <div class="stat-value" style="color:#2E7D32">{{ subjectCount }}</div>
            <div class="stat-label">สาขาวิชาที่เปิดสอน</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined rounded="lg" class="pa-3 text-center stat-card">
            <div class="stat-value" style="color:#424242">{{ entranceYear || "-" }}</div>
            <div class="stat-label">ปีการศึกษาข้อมูลผู้เรียน</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ข้อมูลทั่วไป -->
      <div class="section-heading">
        <v-icon color="#B3151B" class="mr-1">mdi-information-outline</v-icon>ข้อมูลทั่วไป
      </div>
      <v-row dense class="mb-5">
        <v-col cols="12" md="4">
          <CollegeDirector @loaded="onCollegeinfoLoaded" />
        </v-col>
        <v-col cols="12" md="4">
          <CollegeStd02 @loaded="std02 = $event" />
        </v-col>
        <v-col cols="12" md="4">
          <MapThaigis :province-id="collegeinfo.province_ID" />
        </v-col>
      </v-row>

      <!-- ข้อมูลผู้เรียน -->
      <div class="section-heading">
        <v-icon color="#B3151B" class="mr-1">mdi-account-group-outline</v-icon>ข้อมูลผู้เรียน
      </div>
      <v-row dense class="mb-2">
        <v-col cols="12" md="6">
          <StdLevel :rows="studentRows" />
        </v-col>
        <v-col cols="12" md="6">
          <StdBranch :rows="studentRows" />
        </v-col>
      </v-row>
      <v-row dense class="mb-2">
        <v-col cols="12">
          <Stdminor :rows="studentRows" />
        </v-col>
      </v-row>
      <v-row dense class="mb-5">
        <v-col cols="12">
          <Collegemajor :rows="studentRows" :entrance-year="entranceYear" />
        </v-col>
      </v-row>

      <!-- ข้อมูลบุคลากร -->
      <div class="section-heading">
        <v-icon color="#B3151B" class="mr-1">mdi-account-tie-outline</v-icon>ข้อมูลบุคลากร
      </div>
      <v-row dense class="mb-2">
        <v-col cols="12" md="6">
          <ManPowerHrvec @loaded="workforce = $event || []" />
        </v-col>
        <v-col cols="12" md="6">
          <Personnel_etc_id @loaded="teacherBranch = $event || []" />
        </v-col>
      </v-row>
      <v-row dense class="mb-5">
        <v-col cols="12">
          <College_rang @loaded="rangData = $event" />
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import CollegeDirector from "@/views/dashboard/components/collegeinfo/collegeDirector.vue";
import CollegeStd02 from "../components/collegeinfo/collegeStd02.vue";
import MapThaigis from "@/views/dashboard/components/collegeinfo/mapThaigis.vue";
import ManPowerHrvec from "../components/collegeinfo/manPowerHrvec.vue";
import StdLevel from "../components/collegeinfo/stdLevel.vue";
import StdBranch from "../components/collegeinfo/stdBranch.vue";
import Stdminor from "../components/collegeinfo/stdminor.vue";
import Personnel_etc_id from "../components/collegeinfo/personnel_etc_id.vue";
import College_rang from "../components/collegeinfo/college_rang.vue";
import Collegemajor from "../components/collegeinfo/collegemajor.vue";

export default {
  name: "CollegesId",
  components: {
    CollegeDirector,
    CollegeStd02,
    MapThaigis,
    ManPowerHrvec,
    StdLevel,
    StdBranch,
    Stdminor,
    Personnel_etc_id,
    College_rang,
    Collegemajor
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      heroLoading: true,
      exporting: false,

      // รวบรวมจาก event "loaded" ของ widget ลูกแต่ละตัว ใช้ทั้งแสดงผลหัวเพจ/แถบสถิติสรุป และประกอบไฟล์ Excel
      collegeinfo: {},
      std02: {},
      workforce: [],
      teacherBranch: [],
      rangData: null,

      // ข้อมูลผู้เรียน (student_stat_summary_college.php) fetch ที่นี่ครั้งเดียว แล้วส่งต่อ (prop) ให้ทุก
      // widget ที่เกี่ยวกับนักเรียนใช้ร่วมกัน — เดิมแต่ละ widget (4 ตัว) เรียก endpoint เดียวกันแยกกันเอง
      studentRows: [],
      entranceYear: null
    };
  },

  computed: {
    college_ID() {
      return this.$route.query.college_ID;
    },
    todayThai() {
      const d = new Date();
      const thaiMonths = ["มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน","กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม"];
      return `${d.getDate()} ${thaiMonths[d.getMonth()]} ${d.getFullYear() + 543}`;
    },
    totalStudents() {
      return this.studentRows.reduce((sum, r) => sum + (parseInt(r.cnt) || 0), 0);
    },
    subjectCount() {
      return new Set(this.studentRows.map(r => r.minorNameTh).filter(Boolean)).size;
    },
    totalPersonnel() {
      return this.workforce.reduce((sum, r) => sum + (parseInt(r.countGrouptype) || 0), 0);
    },
    totalGovTeachers() {
      return (this.rangData && this.rangData.countAll) || 0;
    }
  },

  watch: {
    "$route.query.college_ID": {
      immediate: false,
      handler() {
        this.loadStudentStats();
      }
    }
  },

  async mounted() {
    if (this.college_ID) {
      await this.loadStudentStats();
    }
    this.heroLoading = false;
  },

  methods: {
    numberWithCommas(x) {
      x = String(x);
      const pattern = /(-?\d+)(\d{3})/;
      while (pattern.test(x)) x = x.replace(pattern, "$1,$2");
      return x;
    },

    onCollegeinfoLoaded(data) {
      this.collegeinfo = data || {};
    },

    async loadStudentStats() {
      try {
        const res = await this.$http.post("student_stat_summary_college.php", {
          ApiKey: this.ApiKey,
          college_id: this.college_ID
        });
        if (res.data && res.data.status) {
          this.studentRows = res.data.rows || [];
          this.entranceYear = res.data.entranceYear;
        } else {
          this.studentRows = [];
          this.entranceYear = null;
        }
      } catch (error) {
        this.studentRows = [];
        this.entranceYear = null;
      }
    },

    // ===== ส่งออก Excel: รวมข้อมูลทุกส่วนของหน้านี้เป็นไฟล์เดียวหลายชีต สำหรับทำรายงาน =====
    exportExcel() {
      this.exporting = true;
      try {
        const wb = XLSX.utils.book_new();
        const c = this.collegeinfo;

        // ชีตข้อมูลทั่วไป
        const generalInfo = [
          { รายการ: "ชื่อสถานศึกษา", ข้อมูล: c.college_name || "" },
          { รายการ: "ชื่อภาษาอังกฤษ", ข้อมูล: c.college_NameEng || "" },
          { รายการ: "ชื่อย่อ", ข้อมูล: c.collegeIni || "" },
          { รายการ: "ประเภทสถานศึกษา", ข้อมูล: c.collegetype_name || "" },
          { รายการ: "อำเภอ", ข้อมูล: c.prefecture_name || "" },
          { รายการ: "จังหวัด", ข้อมูล: c.province_name || "" },
          { รายการ: "ภาค", ข้อมูล: c.region_name || "" },
          { รายการ: "รหัสไปรษณีย์", ข้อมูล: c.college_zipcode || "" },
          { รายการ: "โทรศัพท์", ข้อมูล: c.collegeinfo_phone || c.college_phone || "" },
          { รายการ: "โทรสาร", ข้อมูล: c.collegeinfo_fax || "" },
          { รายการ: "อีเมล", ข้อมูล: c.collegeinfo_email || c.college_email || "" },
          { รายการ: "ที่อยู่", ข้อมูล: c.collegeinfo_address || "" },
          { รายการ: "ผู้อำนวยการ", ข้อมูล: c.collegeinfo_director || "" },
          { รายการ: "เบอร์โทรผู้อำนวยการ", ข้อมูล: c.collegeinfo_directorphone || "" },
          { รายการ: "เว็บไซต์", ข้อมูล: (this.std02 && this.std02.website) || "" }
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(generalInfo), "ข้อมูลทั่วไป");

        // ชีตผู้เรียนตามระดับชั้น
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.aggregate(this.studentRows, "gradeNameTh", "ระดับชั้น")), "ผู้เรียนตามระดับชั้น");

        // ชีตผู้เรียนตามประเภทวิชา
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.aggregate(this.studentRows, "subjectTypeNameTh", "ประเภทวิชา")), "ผู้เรียนตามประเภทวิชา");

        // ชีตผู้เรียนรายละเอียดตามสาขาวิชา (ข้อมูลดิบ ใช้ทำรายงานละเอียด)
        const minorRows = this.studentRows.map(r => ({
          ประเภทวิชา: r.subjectTypeNameTh || "",
          สาขาวิชา: r.minorNameTh || "",
          ระดับชั้น: r.gradeNameTh || "",
          ปีการศึกษา: r.entranceYear || this.entranceYear || "",
          "จำนวน (คน)": parseInt(r.cnt) || 0
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(minorRows), "ผู้เรียนตามสาขาวิชา");

        // ชีตบุคลากรตามประเภท
        const workforceRows = this.workforce.map(w => ({
          ประเภทบุคลากร: w.user_status_name || "",
          "จำนวน (คน)": parseInt(w.countGrouptype) || 0
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(workforceRows), "บุคลากรตามประเภท");

        // ชีตครูผู้สอนตามสาขา
        const teacherRows = this.teacherBranch.map(t => ({
          สาขาวิชา: t.branch_name || "",
          "จำนวน (คน)": parseInt(t.countB) || 0
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(teacherRows), "ครูผู้สอนตามสาขา");

        // ชีตข้าราชการครูฯ ตามวิทยฐานะ
        if (this.rangData && this.rangData.byRank) {
          const rankLabels = ["ครูผู้ช่วย", "ครู", "ชำนาญการ", "ชำนาญการพิเศษ", "เชี่ยวชาญ", "เชี่ยวชาญพิเศษ"];
          const rangRows = rankLabels.map((label, i) => ({
            วิทยฐานะ: label,
            "จำนวน (คน)": this.rangData.byRank[i] || 0
          }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rangRows), "ข้าราชการครูตามวิทยฐานะ");
        }

        const safeName = (c.college_name || "สถานศึกษา").replace(/[\\/:*?"<>|]/g, "");
        XLSX.writeFile(wb, `ข้อมูลสถานศึกษา_${safeName}_${this.college_ID}.xlsx`);
      } catch (error) {
        Swal.fire({ icon: "error", title: "ส่งออกไม่สำเร็จ", text: "กรุณาลองใหม่อีกครั้ง" });
      } finally {
        this.exporting = false;
      }
    },

    // รวมยอด rows ตามคอลัมน์ที่ระบุ คืนเป็น array ของ { [labelKey]: ..., "จำนวน (คน)": ... }
    aggregate(rows, field, labelKey) {
      const map = {};
      rows.forEach(r => {
        const key = r[field] || "ไม่ระบุ";
        map[key] = (map[key] || 0) + (parseInt(r.cnt) || 0);
      });
      return Object.keys(map).map(k => ({ [labelKey]: k, "จำนวน (คน)": map[k] }));
    }
  }
};
</script>

<style scoped>
.collegesid-page {
  max-width: 1320px;
  margin: 0 auto;
}

.hero-card {
  border: 1px solid #e4e4e7 !important;
  background: linear-gradient(135deg, #ffffff 0%, #fff7f7 100%) !important;
}

.breadcrumb-link {
  font-size: 0.82rem;
  color: #6b7280;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.breadcrumb-link:hover {
  color: #b3151b;
}

.college-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1b365d;
  line-height: 1.3;
  margin: 0;
}
.college-name-eng {
  font-size: 0.9rem;
  color: #6b7280;
  margin-top: 2px;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
}

.export-col {
  flex-shrink: 0;
}

.stat-card {
  transition: all 0.2s ease;
}
.stat-value {
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.2;
}
.stat-label {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 2px;
}

.section-heading {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
  border-left: 4px solid #b3151b;
  padding-left: 10px;
  margin: 8px 0 12px;
}

@media (max-width: 960px) {
  .export-col {
    width: 100%;
  }
  .export-col .v-btn {
    width: 100%;
  }
}
</style>
